import { CSSProperties, FunctionComponent } from 'react';
import styles from './ParallaxLayer.module.css';

/** Optional looping animation layered on top of the pointer drift. */
export type FloatAnim = 'drift' | 'bob' | 'pulse';

interface ParallaxLayerProps {
  src: string;
  /** Drift factor: 0 = pinned backdrop, larger = moves more. */
  depth: number;
  /** Stacking order; higher renders in front. */
  z: number;
  float?: FloatAnim;
  /** Seconds; lets sibling layers animate out of phase. */
  floatDelay?: number;
  eager?: boolean;
}

const floatClass: Record<FloatAnim, string> = {
  drift: styles.drift,
  bob: styles.bob,
  pulse: styles.pulse,
};

/**
 * A single decorative art layer. This component renders once and never re-renders
 * during motion: the outer wrapper's drift is driven entirely in CSS from the
 * `--mx`/`--my` variables the hero sets on a shared ancestor, scaled by this
 * layer's `--depth`. The inner image runs its own float keyframes, so the two
 * transforms compose without fighting. No per-frame React work.
 */
const ParallaxLayer: FunctionComponent<ParallaxLayerProps> = ({
  src,
  depth,
  z,
  float,
  floatDelay = 0,
  eager = false,
}) => {
  const wrapperStyle = {
    zIndex: z,
    ['--depth' as string]: depth,
  } as CSSProperties;

  return (
    <div className={styles.layer} style={wrapperStyle} aria-hidden="true">
      <img
        src={src}
        alt=""
        draggable={false}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        className={`${styles.art} ${float ? floatClass[float] : ''}`}
        style={{ animationDelay: `${floatDelay}s` }}
      />
    </div>
  );
};

export default ParallaxLayer;
